import React, { KeyboardEvent, ClipboardEvent, useCallback } from "react"
import Tag from "../Tag/Tag"
import cn from "classnames"
import { useField } from "formik"
import { useTranslation } from "react-i18next"
import { testId } from "../../utility/tests/testId/common"
import "./TagInput.scss"
import { ClassProps } from "../../utility/common/props"
import { formTranslation } from "../../locales/form"

const UP_ARROW_KEY = 38
const DOWN_ARROW_KEY = 40
const ENTER_KEY_CODE = 13
const SPACE_KEY_CODE = 32
const TAB_KEY_CODE = 9
const COMMA_KEY_CODE = 188
const COMMA_AND_DOT_KEY_CODE = 186

export interface TagInputProps extends ClassProps {
    name: string
    placeholder?: string
    splitOnSpaceOrComma?: boolean
    onAddTags?: (count: number) => void
    onRemoveTag?: (index: number) => void
    isInInput?: boolean
    tagContainerClassName?: string
    label?: string
}

const TagInput: React.FC<TagInputProps> = props => {
    const { t } = useTranslation()
    const [field, meta, helpers] = useField<string[]>(props)
    const {
        name,
        placeholder,
        splitOnSpaceOrComma = true,
        className,
        onAddTags,
        onRemoveTag,
        isInInput = true,
        tagContainerClassName,
        label
    } = props
    const { setValue } = helpers
    const { value } = meta

    const removeTag = useCallback(
        (index: number) => {
            setValue([...value.filter((_, i) => i !== index)])
            onRemoveTag && onRemoveTag(index)
        },
        [setValue, value, onRemoveTag]
    )

    const addTags = useCallback(
        (elementValue: string[]) => {
            const valueSet = new Set(value)
            const filteredElements = elementValue.filter(el => !valueSet.has(el))

            setValue([...value, ...filteredElements])
            onAddTags && onAddTags(filteredElements.length)
        },
        [setValue, value, onAddTags]
    )

    const handleAddValue = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            e.preventDefault()

            const elementValue = e.currentTarget.value.trim()
            if (elementValue === "") {
                return
            }

            addTags([elementValue])

            e.currentTarget.value = ""
        },
        [addTags]
    )

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            switch (e.keyCode) {
                case ENTER_KEY_CODE:
                case TAB_KEY_CODE:
                    handleAddValue(e)
                    break
                case COMMA_KEY_CODE:
                case SPACE_KEY_CODE:
                case COMMA_AND_DOT_KEY_CODE:
                    if (splitOnSpaceOrComma) {
                        handleAddValue(e)
                    }

                    break
                case UP_ARROW_KEY:
                case DOWN_ARROW_KEY:
                    e.preventDefault()
                    return
            }
        },
        [handleAddValue, splitOnSpaceOrComma]
    )

    const handlePaste = useCallback(
        (e: ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault()

            const text = e.clipboardData?.getData("text") || ""
            const tags = text
                .split(/[\n,;\s]+/)
                .map(t => t.trim())
                .filter(Boolean)

            if (tags.length > 0) {
                addTags(tags)
            }
        },
        [addTags]
    )

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            field.onBlur(e)

            const elementValue = e.currentTarget.value.trim()
            if (elementValue) {
                addTags([elementValue])
            }

            e.currentTarget.value = ""
        },
        [addTags, field]
    )

    const shouldShowError = meta.touched && !!meta.error
    const shouldShowTags = !isInInput && value.length > 0
    const shouldShowTagContainer = shouldShowError || shouldShowTags

    return (
        <>
            <div className={className}>
                {label && <label htmlFor={name}>{label}</label>}
                <div
                    className={cn(
                        "tag-input",
                        "form-control",
                        meta.touched && !!meta.error ? "is-invalid" : undefined,
                        className
                    )}
                >
                    {isInInput && (
                        <div className="tag-input__tags">
                            {value.map((t, i) => (
                                <Tag
                                    className="tag-input__tag"
                                    key={i}
                                    index={i}
                                    title={t}
                                    onDelete={removeTag}
                                    isInvalid={!!(meta.error && meta.error[i])}
                                />
                            ))}
                        </div>
                    )}
                    <input
                        type="text"
                        name={name}
                        id={name}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        onPaste={handlePaste}
                        placeholder={placeholder}
                        className="tag-input__input"
                        data-testid={testId.tagInput}
                    />
                </div>
            </div>
            {shouldShowTagContainer && (
                <div className={cn("tag-input__tagsContainer", tagContainerClassName && tagContainerClassName)}>
                    {shouldShowError && <div className="tag-input__error">{t(formTranslation.emailInvalid)}</div>}
                    {shouldShowTags && (
                        <div className="tag-input__tags">
                            {value.map((t, i) => (
                                <Tag
                                    className="tag-input__tag"
                                    key={i}
                                    index={i}
                                    title={t}
                                    onDelete={removeTag}
                                    isCompact={false}
                                    isInvalid={!!(meta.error && meta.error[i])}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default TagInput
